import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer'
import { colors, fonts, type, pageGeometry, brand, BRAND_ACCENT } from '../theme'
import { Rule, Spacer, SectionLabel, BodyText, Caption, WireLogo } from '../primitives'

// ─── Types ────────────────────────────────────────────────────────────────────
// Re-exported so the hook and page can import from one place.

export type MetricPoint = {
  label: string
  value: number
}

export type MetricsReportData = {
  answer: string
  thinking: string
  metricLabel: string
  points: MetricPoint[]
  rawRows?: unknown[]
  chartImageUrl?: string
}

export type MetricsReportProps = {
  result: MetricsReportData
  prompt: string
  generatedAt: Date
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

// Compact number formatting: 1500 → "1.5K", 2000000 → "2M"
function formatValue(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`
  if (v >= 10_000)    return `${Math.round(v / 1_000)}K`
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`
  return String(Math.round(v))
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  page: {
    backgroundColor: colors.canvas,
    paddingTop:    pageGeometry.marginTop,
    paddingBottom: pageGeometry.marginBottom,
    paddingLeft:   pageGeometry.marginLeft,
    paddingRight:  pageGeometry.marginRight,
  },

  // ── Header ────────────────────────────────────────────────────────────────
  headerRow: {
    flexDirection: 'row',
    alignItems:    'center',
  },
  brandLockup: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           8,
  },
  brandName: {
    fontFamily: fonts.serif,
    fontSize:   type.size.xl,
    fontWeight: type.weight.bold,
    color:      BRAND_ACCENT,
    letterSpacing: 2,
  },
  headerRight: {
    flexGrow:   1,
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    fontWeight: type.weight.bold,
    color:      colors.fgSecondary,
    letterSpacing: 0.5,
  },
  headerDate: {
    fontFamily: fonts.sans,
    fontSize:   type.size.xs,
    color:      colors.fgMuted,
    marginTop:  2,
  },

  // ── Footer (fixed — repeats on every page) ────────────────────────────────
  footer: {
    position: 'absolute',
    bottom:    24,
    left:      pageGeometry.marginLeft,
    right:     pageGeometry.marginRight,
  },
  footerRow: {
    flexDirection: 'row',
    marginTop:     4,
  },
  footerText: {
    fontFamily: fonts.sans,
    fontSize:   type.size['2xs'],
    color:      colors.fgMuted,
  },
  footerRight: {
    fontFamily: fonts.sans,
    fontSize:   type.size['2xs'],
    color:      colors.fgMuted,
    flexGrow:   1,
    textAlign:  'right',
  },

  // ── Query block ───────────────────────────────────────────────────────────
  queryText: {
    fontFamily: fonts.serif,
    fontSize:   type.size.lg,
    fontStyle:  'italic',
    fontWeight: type.weight.normal,
    lineHeight: type.leading.normal,
    color:      colors.fg,
  },

  // ── Bar chart ─────────────────────────────────────────────────────────────
  barRow: {
    flexDirection: 'row',
    alignItems:    'center',
    marginBottom:  5,
  },
  barLabel: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    color:      colors.fgSecondary,
    textAlign:  'right',
    paddingRight: 8,
  },
  barTrack: {
    backgroundColor: colors.fill,
    borderRadius:    2,
    height:          12,
    overflow:        'hidden',
  },
  barFill: {
    backgroundColor: BRAND_ACCENT,
    borderRadius:    2,
    height:          12,
  },
  barValue: {
    fontFamily: fonts.sans,
    fontSize:   type.size.sm,
    color:      colors.fg,
    paddingLeft: 6,
  },
})

// ─── Styles (table) ──────────────────────────────────────────────────────────
const ts = StyleSheet.create({
  table: {
    width: '100%',
  },
  headerRow: {
    flexDirection:   'row',
    backgroundColor: colors.fill,
    borderTopLeftRadius:  2,
    borderTopRightRadius: 2,
  },
  row: {
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    borderBottomColor: colors.lineSubtle,
  },
  rowAlt: {
    backgroundColor: colors.surface,
  },
  cell: {
    flex:        1,
    paddingHorizontal: 8,
    paddingVertical:   5,
    fontFamily:  fonts.sans,
    fontSize:    type.size.sm,
    color:       colors.fg,
  },
  headerCell: {
    flex:         1,
    paddingHorizontal: 8,
    paddingVertical:   5,
    fontFamily:   fonts.sans,
    fontSize:     type.size['2xs'],
    fontWeight:   type.weight.bold,
    color:        colors.fgSecondary,
    letterSpacing: 0.8,
  },
})

// ─── Data table ───────────────────────────────────────────────────────────────

function DataTable({ rows }: { rows: unknown[] }) {
  if (rows.length === 0) return null
  const first = rows[0] as Record<string, unknown>
  const cols = Object.keys(first).filter(
    (k) => typeof first[k] === 'string' || typeof first[k] === 'number',
  )
  if (cols.length === 0) return null

  return (
    <View style={ts.table}>
      {/* Header */}
      <View style={ts.headerRow}>
        {cols.map((col) => (
          <Text key={col} style={ts.headerCell}>{col.toUpperCase()}</Text>
        ))}
      </View>
      {/* Body */}
      {rows.map((row, i) => {
        const r = row as Record<string, unknown>
        return (
          <View key={i} style={[ts.row, i % 2 === 1 ? ts.rowAlt : {}]}>
            {cols.map((col) => (
              <Text key={col} style={ts.cell}>{String(r[col] ?? '')}</Text>
            ))}
          </View>
        )
      })}
    </View>
  )
}

// ─── Chart dimensions (pt) ────────────────────────────────────────────────────
// Content width = 595 (A4) − 48 (left) − 48 (right) = 499 pt
// Allocate: label column + track + value column = 499
const LABEL_W  = 110  // pt — max label text column
const VALUE_W  = 38   // pt — right-side value number column
const TRACK_W  = 499 - LABEL_W - VALUE_W - 8 - 6  // 8 = paddingRight, 6 = paddingLeft → ≈ 337 pt

// ─── Horizontal bar chart ─────────────────────────────────────────────────────

function BarChart({ points, label }: { points: MetricPoint[]; label: string }) {
  const maxValue = Math.max(...points.map((p) => p.value), 1)

  return (
    <View>
      <SectionLabel>{label}</SectionLabel>
      {points.map((p, i) => {
        const fillWidth = Math.max(2, Math.round((p.value / maxValue) * TRACK_W))
        return (
          <View key={i} style={s.barRow}>
            <Text style={[s.barLabel, { width: LABEL_W }]}>{p.label}</Text>
            <View style={[s.barTrack, { width: TRACK_W }]}>
              <View style={[s.barFill, { width: fillWidth }]} />
            </View>
            <Text style={[s.barValue, { width: VALUE_W }]}>{formatValue(p.value)}</Text>
          </View>
        )
      })}
    </View>
  )
}

// ─── Document ─────────────────────────────────────────────────────────────────

export function MetricsReport({ result, prompt, generatedAt }: MetricsReportProps) {
  const hasChart = result.points.length > 0
  const hasTable = (result.rawRows?.length ?? 0) > 0

  return (
    <Document
      title="Metrics Analysis"
      author={brand.name}
      subject={prompt}
      creator={`${brand.name} ${brand.tagline}`}
    >
      <Page size={pageGeometry.size} style={s.page}>

        {/* ── Fixed footer — repeated on every page ── */}
        <View style={s.footer} fixed>
          <Rule />
          <View style={s.footerRow}>
            <Text style={s.footerText}>
              {`${brand.name} ${brand.tagline}  ·  Generated ${formatDate(generatedAt)}`}
            </Text>
            <Text
              style={s.footerRight}
              render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
            />
          </View>
        </View>

        {/* ── Header ── */}
        <View style={s.headerRow}>
          <View style={s.brandLockup}>
            <WireLogo height={22} />
            <Text style={s.brandName}>{brand.name}</Text>
          </View>
          <View style={s.headerRight}>
            <Text style={s.headerTitle}>Metrics Analysis</Text>
            <Text style={s.headerDate}>{formatDate(generatedAt)}</Text>
          </View>
        </View>
        <Spacer size={2} />
        <Rule accent />

        <Spacer size={6} />

        {/* ── Query ── */}
        {prompt.trim() !== '' && (
          <>
            <SectionLabel>Query</SectionLabel>
            <Text style={s.queryText}>{prompt}</Text>
            <Spacer size={6} />
            <Rule />
            <Spacer size={6} />
          </>
        )}

        {/* ── Chart ── */}
        {hasChart && (
          <>
            {result.chartImageUrl ? (
              <View>
                <SectionLabel>{result.metricLabel}</SectionLabel>
                <Image src={result.chartImageUrl} style={{ width: '100%', objectFit: 'contain' }} />
              </View>
            ) : (
              <BarChart points={result.points} label={result.metricLabel} />
            )}
            <Spacer size={6} />
            <Rule />
            <Spacer size={6} />
          </>
        )}

        {/* ── Data table ── */}
        {hasTable && (
          <>
            <SectionLabel>Data</SectionLabel>
            <DataTable rows={result.rawRows!} />
            <Spacer size={6} />
            <Rule />
            <Spacer size={6} />
          </>
        )}

        {/* ── Analysis ── */}
        <SectionLabel>Analysis</SectionLabel>
        <BodyText>{result.answer}</BodyText>

        {/* ── No-data notice (prose-only response) ── */}
        {!hasChart && !hasTable && (
          <>
            <Spacer size={4} />
            <Caption>No quantitative data was returned for this query.</Caption>
          </>
        )}

      </Page>
    </Document>
  )
}
